import { createSessionLoadContext } from '../session/session-load-context';
import { getMessagesDiv } from './chat/dom';
import { createHistoryBannerController } from './history-banner';
import { shouldAutoloadHistory } from './history-autoload';
import { logCodexSteeringDebug, logScrollDebug, logSessionDebug, logSessionLoadDebug } from './session-debug';

const HISTORY_REQUEST_TIMEOUT_MS = 6000;

type AppLifecycleOptions = {
  loginForm: HTMLFormElement;
  loginPassword: HTMLInputElement;
  loginError: HTMLElement;
  loginOverlay: HTMLElement;
  app: HTMLElement;
  rememberPw: HTMLInputElement | null;
  getLoginPending: () => boolean;
  setLoginPendingState: (pending: boolean, message?: string) => void;
  clearLoginPendingState: () => void;
  getLoginAttemptToken: () => number;
  setLoginAttemptToken: (value: number) => void;
  setPendingAuthPassword: (value: string | null) => void;
  setLoginTimeoutId: (value: number | null) => void;
  waitForSocketOpen: () => Promise<void>;
  getSocket: () => { readyState?: number } | null;
  send: (payload: any) => void;
  requestNotificationPermission: () => void;
  clearRememberedPassword: () => void;
  restoreRememberedPasswordInput: () => void;
  connect: () => void;
  connectionState: any;
  sessionState: any;
  composeState: any;
  getPendingOutgoingMessage: () => any;
  retryPendingOutgoingMessage: (reason?: string) => void;
  sessionExportSnapshots: Map<string, any>;
  deepClone: (value: any) => any;
  maybeConfirmPendingOutgoingMessage: (messages: any[]) => void;
  openSession: (id: string, options?: any) => void;
  getLastVisibilityResyncAt: () => number;
  setLastVisibilityResyncAt: (value: number) => void;
  visibilityResyncThrottleMs: number;
  updateCwdBadge: () => void;
  updateSessionItemLoadedMessageCount?: (sessionId: string) => void;
  rememberedPasswordStorageKey: string;
};

export function createAppLifecycleController(options: AppLifecycleOptions) {
  let pendingHistoryAutoloadFrame = 0;
  let historyRequestTimeoutId = 0;
  let historyRequestSessionId = '';
  let historyRequestSeq = 0;
  const historyBanner = createHistoryBannerController({
    onRetry: () => requestMoreHistory('banner_retry'),
  });

  function clearHistoryRequestTimeout(): void {
    if (!historyRequestTimeoutId) return;
    clearTimeout(historyRequestTimeoutId);
    historyRequestTimeoutId = 0;
    historyRequestSessionId = '';
  }

  function scheduleHistoryRequestTimeout(sessionId: string): void {
    clearHistoryRequestTimeout();
    historyRequestSessionId = sessionId;
    historyRequestTimeoutId = window.setTimeout(() => {
      historyRequestTimeoutId = 0;
      if (historyRequestSessionId !== sessionId) return;
      if (options.sessionState.currentSessionId !== sessionId) return;
      if (!options.sessionState.currentHistoryLoading) return;
      options.sessionState.currentHistoryLoading = false;
      logSessionDebug('history-banner:request-timeout', {
        sessionId,
        historyBuffered: options.sessionState.currentHistoryBuffered,
        historyTotal: options.sessionState.currentHistoryTotal,
      });
      historyBanner.showError(
        options.sessionState.currentHistoryBuffered,
        options.sessionState.currentHistoryTotal,
      );
    }, HISTORY_REQUEST_TIMEOUT_MS);
  }

  function syncHistoryUi(reason: string): void {
    requestAnimationFrame(() => {
      const messagesDiv = getMessagesDiv();
      if (!messagesDiv || !options.sessionState.currentSessionId) {
        logSessionDebug('history-banner:sync-skip', {
          reason,
          skipReason: !messagesDiv ? 'missing_messages_div' : 'missing_current_session',
        });
        historyBanner.hide();
        return;
      }
      const autoloadEligible = shouldAutoloadHistory({
        historyPending: !!options.sessionState.currentHistoryPending,
        currentHistoryLoading: !!options.sessionState.currentHistoryLoading,
        scrollTop: messagesDiv.scrollTop,
        scrollHeight: messagesDiv.scrollHeight,
        clientHeight: messagesDiv.clientHeight,
      });
      logSessionDebug('history-banner:sync', {
        reason,
        sessionId: options.sessionState.currentSessionId,
        historyPending: !!options.sessionState.currentHistoryPending,
        currentHistoryLoading: !!options.sessionState.currentHistoryLoading,
        historyBuffered: options.sessionState.currentHistoryBuffered,
        historyTotal: options.sessionState.currentHistoryTotal,
        isGenerating: !!options.composeState.isGenerating,
        scrollTop: messagesDiv.scrollTop,
        scrollHeight: messagesDiv.scrollHeight,
        clientHeight: messagesDiv.clientHeight,
        autoloadEligible,
        bannerStatus: historyBanner.getStatus(),
      });
      if (options.sessionState.currentHistoryLoading) {
        historyBanner.showLoading(
          options.sessionState.currentHistoryBuffered,
          options.sessionState.currentHistoryTotal,
        );
        return;
      }
      if (!options.sessionState.currentHistoryPending) {
        clearHistoryRequestTimeout();
        if (options.sessionState.currentHistoryTotal > 0) {
          historyBanner.showComplete(
            options.sessionState.currentHistoryBuffered,
            options.sessionState.currentHistoryTotal,
          );
        } else {
          historyBanner.hide();
        }
        return;
      }
      if (
        reason === 'session_history_chunk'
        && historyBanner.getStatus() === 'loading'
      ) {
        historyBanner.showProgress(
          options.sessionState.currentHistoryBuffered,
          options.sessionState.currentHistoryTotal,
        );
        return;
      }
      if (historyBanner.getStatus() === 'error') return;
      if (historyBanner.getStatus() === 'progress') return;
      if (!autoloadEligible) {
        logSessionDebug('history-banner:hide-noneligible', {
          reason,
          sessionId: options.sessionState.currentSessionId,
          historyBuffered: options.sessionState.currentHistoryBuffered,
          historyTotal: options.sessionState.currentHistoryTotal,
          scrollTop: messagesDiv.scrollTop,
          scrollHeight: messagesDiv.scrollHeight,
          clientHeight: messagesDiv.clientHeight,
          bannerStatus: historyBanner.getStatus(),
        });
        historyBanner.hide();
        return;
      }
      requestMoreHistory(reason);
    });
  }

  function requestMoreHistory(reason: string) {
    if (!options.sessionState.currentSessionId) {
      logSessionDebug('history-banner:request-skip', {
        reason,
        skipReason: 'missing_current_session',
      });
      return;
    }
    if (!options.sessionState.currentHistoryPending) {
      logSessionDebug('history-banner:request-skip', {
        reason,
        sessionId: options.sessionState.currentSessionId,
        skipReason: 'history_not_pending',
        historyBuffered: options.sessionState.currentHistoryBuffered,
        historyTotal: options.sessionState.currentHistoryTotal,
      });
      return;
    }
    if (options.sessionState.currentHistoryLoading) {
      logSessionDebug('history-banner:request-skip', {
        reason,
        sessionId: options.sessionState.currentSessionId,
        skipReason: 'already_loading',
        historyBuffered: options.sessionState.currentHistoryBuffered,
        historyTotal: options.sessionState.currentHistoryTotal,
      });
      return;
    }
    historyRequestSeq += 1;
    options.sessionState.currentHistoryRequestSeq = historyRequestSeq;
    const requestStartedAt = Date.now();
    const activeLoad = options.sessionState.activeSessionLoad;
    if (activeLoad?.sessionId === options.sessionState.currentSessionId) {
      activeLoad.historyRequestSeq = historyRequestSeq;
      activeLoad.historyRequestStartedAt = requestStartedAt;
    }
    logSessionDebug('history-banner:request', {
      requestSeq: historyRequestSeq,
      reason,
      sessionId: options.sessionState.currentSessionId,
      historyPending: !!options.sessionState.currentHistoryPending,
      currentHistoryLoading: !!options.sessionState.currentHistoryLoading,
      historyBuffered: options.sessionState.currentHistoryBuffered,
      historyTotal: options.sessionState.currentHistoryTotal,
      isGenerating: !!options.composeState.isGenerating,
    });
    options.sessionState.currentHistoryLoading = true;
    historyBanner.showLoading(
      options.sessionState.currentHistoryBuffered,
      options.sessionState.currentHistoryTotal,
    );
    scheduleHistoryRequestTimeout(options.sessionState.currentSessionId);
    logScrollDebug('history-autoload:request', {
      requestSeq: historyRequestSeq,
      reason,
      sessionId: options.sessionState.currentSessionId,
      historyBuffered: options.sessionState.currentHistoryBuffered,
      historyTotal: options.sessionState.currentHistoryTotal,
    });
    logSessionLoadDebug('history:request-sent', {
      requestSeq: historyRequestSeq,
      reason,
      sessionId: options.sessionState.currentSessionId,
      loadedCount: options.sessionState.currentHistoryBuffered,
      historyRemaining: Math.max(0, options.sessionState.currentHistoryTotal - options.sessionState.currentHistoryBuffered),
    });
    options.send({
      type: 'request_session_history',
      sessionId: options.sessionState.currentSessionId,
      loadedCount: options.sessionState.currentHistoryBuffered,
      requestSeq: historyRequestSeq,
    });
  }

  function scheduleHistoryAutoload(reason: string) {
    if (pendingHistoryAutoloadFrame) cancelAnimationFrame(pendingHistoryAutoloadFrame);
    pendingHistoryAutoloadFrame = requestAnimationFrame(() => {
      pendingHistoryAutoloadFrame = 0;
      syncHistoryUi(reason);
    });
  }

  async function doLogin() {
    if (options.getLoginPending()) return;
    const pw = options.loginPassword.value;
    if (!pw) return;
    const attemptToken = Date.now();
    options.setLoginAttemptToken(attemptToken);
    options.setPendingAuthPassword(pw);
    options.loginError.hidden = true;
    options.setLoginPendingState(true);
    const timeoutId = window.setTimeout(() => {
      if (options.getLoginAttemptToken() !== attemptToken) return;
      options.setPendingAuthPassword(null);
      options.setLoginPendingState(false, '登录超时，请重试');
    }, 12000);
    options.setLoginTimeoutId(timeoutId);
    try {
      await options.waitForSocketOpen();
      if (options.getLoginAttemptToken() !== attemptToken) return;
      const socket = options.getSocket();
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        throw new Error('连接未就绪，请稍后重试');
      }
      options.send({ type: 'auth', password: pw });
    } catch (error: any) {
      if (options.getLoginAttemptToken() !== attemptToken) return;
      options.setPendingAuthPassword(null);
      options.setLoginPendingState(false, error?.message || '连接失败，请稍后重试');
    }
    options.requestNotificationPermission();
  }

  function bindLoginHandlers() {
    options.loginForm.addEventListener('submit', (e) => {
      e.preventDefault();
      doLogin();
    });
    options.loginForm.querySelector('button[type="submit"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      doLogin();
    });
    options.rememberPw?.addEventListener('change', () => {
      if (!options.rememberPw?.checked) options.clearRememberedPassword();
    });
  }

  function bindGlobalLifecycleHandlers() {
    document.addEventListener('webcoding-auth-restored', () => {
      options.clearLoginPendingState();
      const pendingOutgoingMessage = options.getPendingOutgoingMessage();
      if (!pendingOutgoingMessage) return;
      if (
        pendingOutgoingMessage.sessionId &&
        options.sessionState.currentSessionId &&
        pendingOutgoingMessage.sessionId !== options.sessionState.currentSessionId
      ) return;
      setTimeout(() => options.retryPendingOutgoingMessage('auth-restored'), 1200);
    });
    document.addEventListener('webcoding-auth-failed', () => {
      options.clearLoginPendingState();
    });
    document.addEventListener('webcoding-session-snapshot', (event: any) => {
      const snapshot = event?.detail?.snapshot || null;
      const messages = Array.isArray(snapshot?.messages) ? snapshot.messages : [];
      const pending = options.getPendingOutgoingMessage();
      logCodexSteeringDebug('ui:session-snapshot-received', {
        sessionId: snapshot?.sessionId || null,
        currentSessionId: options.sessionState.currentSessionId || null,
        messageCount: messages.length,
        userMessageCount: messages.filter((message: any) => message?.role === 'user').length,
        pendingClientMessageId: pending?.clientMessageId || null,
        pendingMessagePresent: !!pending && messages.some((message: any) => message?.clientMessageId === pending.clientMessageId),
        isRunning: !!snapshot?.isRunning,
      });
      if (snapshot?.sessionId) options.sessionExportSnapshots.set(snapshot.sessionId, options.deepClone(snapshot));
      options.maybeConfirmPendingOutgoingMessage(event?.detail?.messages || []);
      const shouldCheckAutoload = !!(
        snapshot?.sessionId
        && (
          snapshot.sessionId === options.sessionState.currentSessionId
          || snapshot.sessionId === options.sessionState.activeSessionLoad?.sessionId
        )
      );
      if (shouldCheckAutoload) {
        scheduleHistoryAutoload('session_snapshot');
      }
    });
    document.addEventListener('webcoding-session-history-chunk', (event: any) => {
      const sessionId = String(event?.detail?.sessionId || '').trim();
      const messages = Array.isArray(event?.detail?.messages) ? event.detail.messages : [];
      if (!sessionId || messages.length === 0) return;
      logSessionDebug('history-banner:chunk-received', {
        sessionId,
        messageCount: messages.length,
        currentHistoryBuffered: options.sessionState.currentHistoryBuffered,
        currentHistoryTotal: options.sessionState.currentHistoryTotal,
      });
      const existing = options.sessionExportSnapshots.get(sessionId);
      if (existing) {
        existing.messages = messages.concat(Array.isArray(existing.messages) ? existing.messages : []);
        options.sessionExportSnapshots.set(sessionId, existing);
      }
      if (sessionId === options.sessionState.currentSessionId) {
        const activeLoad = options.sessionState.activeSessionLoad;
        const requestStartedAt = activeLoad?.sessionId === sessionId ? activeLoad.historyRequestStartedAt : 0;
        logSessionLoadDebug('history:response-received', {
          requestSeq: event?.detail?.requestSeq
            ?? (activeLoad?.sessionId === sessionId ? activeLoad.historyRequestSeq : null),
          sessionId,
          messageCount: messages.length,
          durationMs: requestStartedAt ? Date.now() - requestStartedAt : null,
          remaining: event?.detail?.remaining ?? null,
        });
        clearHistoryRequestTimeout();
      }
    });
    document.addEventListener('webcoding-history-state-changed', (event: any) => {
      const sessionId = String(event?.detail?.sessionId || '').trim();
      if (!sessionId) return;
      if (sessionId !== options.sessionState.currentSessionId) return;
      const source = String(event?.detail?.source || 'history_state_changed');
      logSessionDebug('history-banner:state-changed', {
        source,
        sessionId,
        historyTotal: event?.detail?.historyTotal ?? null,
        historyBuffered: event?.detail?.historyBuffered ?? null,
        historyPending: event?.detail?.historyPending ?? null,
        historyLoading: event?.detail?.historyLoading ?? null,
        activeLoadSessionId: options.sessionState.activeSessionLoad?.sessionId || null,
      });
      options.updateSessionItemLoadedMessageCount?.(sessionId);
      // 快照已明确告知还有历史时，不能依赖滚动位置触发首次分页。
      if (
        source === 'apply_session_snapshot'
        && event?.detail?.historyPending
        && !event?.detail?.historyLoading
        && options.sessionState.activeSessionLoad?.sessionId === sessionId
      ) {
        requestMoreHistory('apply_session_snapshot');
      }
      scheduleHistoryAutoload(source);
    });
    document.addEventListener('webcoding-history-top-visible', (event: any) => {
      logSessionDebug('history-banner:top-visible', {
        sessionId: options.sessionState.currentSessionId || null,
        historyPending: !!options.sessionState.currentHistoryPending,
        currentHistoryLoading: !!options.sessionState.currentHistoryLoading,
        historyBuffered: options.sessionState.currentHistoryBuffered,
        historyTotal: options.sessionState.currentHistoryTotal,
        scrollTop: event?.detail?.scrollTop ?? null,
        scrollHeight: event?.detail?.scrollHeight ?? null,
        clientHeight: event?.detail?.clientHeight ?? null,
      });
      syncHistoryUi('top_visible');
    });

    window.addEventListener('resize', options.updateCwdBadge);
    window.addEventListener('online', () => {
      options.connectionState.reconnectAttempts = 0;
      if (!options.connectionState.ws || options.connectionState.ws.readyState > WebSocket.OPEN) options.connect();
    });

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch((error) => {
        console.warn('[WEBCODING-SW]', 'service worker register failed', error);
      });
    }

    localStorage.removeItem('webcoding-pw');
    localStorage.removeItem(options.rememberedPasswordStorageKey);
    options.restoreRememberedPasswordInput();

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - options.getLastVisibilityResyncAt() < options.visibilityResyncThrottleMs) return;
      options.setLastVisibilityResyncAt(now);
      logScrollDebug('visibilitychange:visible', {
        currentSessionId: options.sessionState.currentSessionId || null,
        wsReadyState: options.connectionState.ws?.readyState ?? null,
        isGenerating: !!options.composeState.isGenerating,
        currentSessionRunning: !!options.sessionState.currentSessionRunning,
      });
      if (!options.connectionState.ws || options.connectionState.ws.readyState > WebSocket.OPEN) {
        options.connect();
      } else if (options.connectionState.ws.readyState === WebSocket.OPEN && options.sessionState.currentSessionId) {
        logScrollDebug('visibilitychange:open-session', {
          sessionId: options.sessionState.currentSessionId,
          reason: 'foreground_resync',
        });
        historyBanner.hide();
        options.openSession(options.sessionState.currentSessionId, {
          forceSync: true,
          blocking: false,
          context: createSessionLoadContext('foreground_resync'),
        });
      }
    });

    if (!options.connectionState.authToken) {
      clearHistoryRequestTimeout();
      historyBanner.hide();
      options.loginOverlay.hidden = false;
      options.app.hidden = true;
      options.restoreRememberedPasswordInput();
    } else {
      options.loginOverlay.hidden = true;
      options.app.hidden = false;
    }
  }

  return {
    bindLoginHandlers,
    bindGlobalLifecycleHandlers,
  };
}
